import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { isPageLoading, subscribePageLoad } from '../utils/pageLoadActivity'
import { reachableRouteComponents } from './preloadRoutes'

// Let response handlers render and start dependent/debounced requests before
// calling the page ready. Pending data requests AND foreground lazy imports
// must have settled; this is not a timer started at login or window.load.
const SETTLE_MS = 1000

export function startRoutePreloading({ root, context, currentUrl }) {
    const attempted = new Set()
    let stopped = false
    let importing = false
    let timer
    let idle
    let frame
    let readyAt = Date.now() + SETTLE_MS

    const cancelScheduled = () => {
        clearTimeout(timer)
        if (idle !== undefined) window.cancelIdleCallback(idle)
        cancelAnimationFrame(frame)
        idle = undefined
    }
    const blocked = () => document.hidden || navigator.onLine === false || isPageLoading() || !!root.querySelector('[data-route-loading]')

    const loadNext = () => {
        idle = undefined
        if (stopped || importing || blocked()) return
        const next = reachableRouteComponents(root, context, currentUrl).find(component => !attempted.has(component) && !component.isPreloaded?.())
        if (!next) return
        if (attempted.size === 0) performance.mark?.('route-preload:start')
        attempted.add(next)
        importing = true
        // Warm only the route module, never nested lazy widgets or backend data.
        next.preload({ background: true, dependencies: false }).catch(() => {
            // Navigation can retry. A failed speculation must not reload the tab,
            // consume stale-deploy recovery, or become an unhandled rejection.
        }).finally(() => {
            importing = false
            scheduleNext()
        })
    }

    const scheduleNext = () => {
        cancelScheduled()
        if (stopped || importing || blocked()) return
        timer = setTimeout(() => {
            if (blocked()) return
            // Two frames leave a paint for the page before starting more work.
            frame = requestAnimationFrame(() => {
                frame = requestAnimationFrame(() => {
                    if (window.requestIdleCallback) idle = window.requestIdleCallback(loadNext)
                    else timer = setTimeout(loadNext, 0)
                })
            })
        }, Math.max(0, readyAt - Date.now()))
    }

    const schedule = () => {
        readyAt = Date.now() + SETTLE_MS
        scheduleNext()
    }

    const unsubscribe = subscribePageLoad(schedule)
    const destinationSelector = 'a[href], [data-preload-route], [data-route-loading]'
    const containsDestination = node => node.nodeType === 1 && (node.matches(destinationSelector) || node.querySelector(destinationSelector))
    const observer = new MutationObserver(records => {
        // Ignore unrelated text updates (e.g. a clock) so they cannot perpetually
        // postpone warming. Request/import activity already gates data rendering.
        if (records.some(record => record.type === 'attributes' || [...record.addedNodes, ...record.removedNodes].some(containsDestination))) schedule()
    })
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['href', 'data-preload-route', 'data-preload-mode', 'disabled', 'aria-disabled', 'data-route-loading'] })
    document.addEventListener('visibilitychange', schedule)
    window.addEventListener('online', schedule)
    window.addEventListener('offline', schedule)
    schedule()
    return () => {
        stopped = true
        cancelScheduled()
        unsubscribe()
        observer.disconnect()
        document.removeEventListener('visibilitychange', schedule)
        window.removeEventListener('online', schedule)
        window.removeEventListener('offline', schedule)
    }
}

export default function RoutePreloader({ enabled, user, primaryUI }) {
    const location = useLocation()
    useEffect(() => {
        if (!enabled) return
        return startRoutePreloading({ root: document.body, context: { user, primaryUI }, currentUrl: window.location.href })
    }, [enabled, user, primaryUI, location.pathname, location.search])
    return null
}
